import '../../styles/carl-typography.css';
import '../../components/blog/blog-typography.module.css';

import React from 'react';
import ReactPlayer from 'react-player';

import { AlmaLink } from '../alma-link';
import { AnchorLink } from '../anchor-link';
import { BannerBlog } from '../bannerBlog';
import { BlogPortableText } from '../blog-portable-text';
import { BrandIcon } from '../brand-icon';
import { IterableEmbed } from '../iterableEmbed';
import { MainImage } from '../mainImage';
import { WistiaEmbed } from '../wistia';
import { PortableText } from './portable-text';
import * as styles from './serializers.module.css';
import classNames from 'classnames';

const AuthorReference = ({ node }) => {
	if (node.author?.name) {
		return <span>{node.author.name}</span>;
	}
	return <></>;
};

const Callout = ({ text }) => {
	return <PortableText blocks={text} className={styles.callout} />;
};

const DecorativeSpan = ({ children, mark }) => {
	const { svgUrl, svg, scale, positionX, positionY } = mark;
	const spanStyle = {
		'--position-x': `${positionX}%`,
		'--position-y': `${positionY}%`,
		'--scale': `${scale}%`,
		'--svg-url': `${svgUrl}`,
		'z-index': '1',
	};
	return (
		<span className={styles.decorativeSpan} style={spanStyle}>
			<span dangerouslySetInnerHTML={{ __html: svg }} />
			{children}
		</span>
	);
};

const HorizontalLine = ({ node }) => {
	const hrColor = node.hrColor || '';
	const hrWidth = node.hrWidth || '100';

	return (
		<hr
			className={hrColor}
			style={{
				height: '1.5px',
				margin: '1.5rem auto',
				width: `${hrWidth}%`,
			}}
		/>
	);
};

const MockTable = ({ rows }) => {
	if (!rows || rows.length === 0) {
		return null;
	}

	return (
		<table className={styles.portableTable}>
			<tbody>
				{rows?.map((row, rowIndex) => (
					<tr key={`row-${rowIndex}`}>
						{row.cells?.map((cell, cellIndex) => (
							<td key={`cell-${rowIndex}-${cellIndex}`}>
								<PortableText blocks={cell?.content} />
							</td>
						))}
					</tr>
				))}
			</tbody>
		</table>
	);
};

const Quote = ({ author, link, quote, source }) => {
	return (
		<figure className={styles.quoteText}>
			<blockquote>
				<BlogPortableText blocks={quote} />
			</blockquote>
			{source ? (
				<figcaption>
					&mdash;{' '}
					{link ? (
						<a
							href={link}
							rel='noopener noreferrer'
							target='_blank'
						>
							{source}
						</a>
					) : (
						source
					)}
				</figcaption>
			) : author ? (
				<figcaption>
					&mdash;{' '}
					<a href={`/blog/authors/${author.slug?.current}/`}>
						{author.name}
					</a>
					{author.specialty && `, ${author.specialty}`}
				</figcaption>
			) : null}
		</figure>
	);
};

// TODO: remove after all instances are switched over
const TextColor = ({ children, mark }) => {
	return <span className={mark.textColor}>{children}</span>;
};

const TextStyles = ({ children, mark }) => {
	const { removeMaxWidth, textBalance, textClass, textColor } = mark;

	// Determine classes to apply based on boolean properties
	const classes = [
		textColor,
		textClass,
		textBalance && 'textBalance',
		removeMaxWidth && 'removeMaxWidth',
	]
		.filter(Boolean)
		.join(' ');

	return <span className={classes}>{children}</span>;
};

export const serializers = {
	types: {
		anchorDestination: ({ node }) => {
			const { anchorLinkId } = node;
			return (
				<span
					className={styles.anchorDestination}
					id={anchorLinkId}
				></span>
			);
		},
		authorReference: AuthorReference,
		bannerBlog: ({ node }) => <BannerBlog {...node} />,
		brandIconComponent: ({ node }) => <BrandIcon icon={node} />,
		callout: ({ node }) => <Callout {...node} />,
		cta: ({ node }) => {
			return (
				<div className={styles.callToAction}>
					<AlmaLink cta={{ ...node }} />
				</div>
			);
		},
		horizontalLine: HorizontalLine,
		iterableForm: ({ node }) => <IterableEmbed iterableForm={node} />,
		mainImage: ({ node }) => <MainImage mainImage={node} />,
		mockTable: ({ node }) => <MockTable {...node} />,
		quote: ({ node }) => <Quote {...node} />,
		videoEmbed: ({ node }) => (
			<ReactPlayer
				className={styles.reactPlayerContainer}
				url={node.url}
				controls
			/>
		),
		wistiaEmbed: ({ node }) => <WistiaEmbed {...node} />,
	},
	marks: {
		body: ({ children }) => <span className='body'>{children}</span>,
		bodyDisplay: ({ children }) => (
			<span className='body-display'>{children}</span>
		),
		bodyDisplaySm: ({ children }) => (
			<span className='body-display-sm'>{children}</span>
		),
		bodyLarge: ({ children }) => <span className='body-l'>{children}</span>,
		bodySmCaps: ({ children }) => (
			<span className='body-sm-caps'>{children}</span>
		),
		bodyXL: ({ children }) => <span className='body-xl'>{children}</span>,
		decorativeSpan: DecorativeSpan,
		fontAdjustments: ({ children }) => {
			console.log('Unknown mark - fontAdjustments: ', children);
		},
		heading2X: ({ children }) => (
			<span className='heading-2x'>{children}</span>
		),
		headingL: ({ children }) => (
			<span className='heading-l'>{children}</span>
		),
		headingM: ({ children }) => (
			<span className='heading-m'>{children}</span>
		),
		headingS: ({ children }) => (
			<span className='heading-s'>{children}</span>
		),
		headingXL: ({ children }) => (
			<span className='heading-xl'>{children}</span>
		),
		headingXS: ({ children }) => (
			<span className='heading-xs'>{children}</span>
		),
		link: ({ children, mark }) => {
			const openInNewTab = mark.openInNewTab === true;
			const removeUnderline = mark.removeUnderline === true;
			return (
				<a
					className={classNames(
						styles.textLink,
						removeUnderline && styles.removeLinkUnderline
					)}
					target={openInNewTab ? '_blank' : '_self'}
					rel={openInNewTab ? 'noopener noreferrer' : undefined}
					href={mark.href}
				>
					{children}
				</a>
			);
		},
		textColor: TextColor,
		textStyles: TextStyles,
	},
};
